import { useRouter } from 'next/router'
import * as qs from 'qs'
import * as React from 'react'
import { useEffect } from 'react'

import { useContent, useQuery } from '@ngb-frontend/shared/context'
import { ErrorVariants, UserType } from '@ngb-frontend/shared/types'
import {
  NotificationViewError,
  StepLayout as Layout,
} from '@ngb-frontend/shared/ui'
import { useTracking } from '@ngb-frontend/shared/utils'

import type { GetServerSideProps, NextPage } from 'next'

export type PageProps = { type: ErrorVariants }

const ErrorPage: NextPage<PageProps> = ({ type }) => {
  const router = useRouter()
  const query = useQuery()
  const c = useContent()
  // Drivers stay inside the flow: its title in the top bar instead of the logo
  const isDriver = query?.userType === UserType.Driver
  const { trackPageView } = useTracking(query)
  const onClose = React.useCallback(
    () => router.push(query?.closeUrl || ''),
    [query?.closeUrl, router],
  )

  // `retry=true` is set by index.page only for transient session-request
  // failures. Retry hard-reloads the app at the first step with the original
  // query params so the bootstrapping calls (vehicle/driver/booking) re-run
  // with a fresh cache.
  const canRetry = router.query.retry === 'true'
  const handleRetry = React.useCallback(() => {
    const { licensePlate, ...rest } = query?.bare || {}
    window.location.assign(
      `/?${qs.stringify({ ...rest, registration: licensePlate })}`,
    )
  }, [query?.bare])

  const errorVariant = Object.values(ErrorVariants).includes(type)
    ? type
    : ErrorVariants.Generic

  useEffect(() => {
    if (!window.location.href.includes(type)) {
      // for cases where the component is rendered without routing
      router.push(`/error/${type}`, undefined, { shallow: true })
    } else trackPageView({ displayName: errorVariant })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout
      navLabel={isDriver ? c('steps.summary.navLabelDriver') : ''}
      showLogo={!isDriver}
    >
      <NotificationViewError
        variant={
          Object.values(ErrorVariants).includes(type)
            ? type
            : ErrorVariants.Generic
        }
        type={query?.userType || UserType.Unknown}
        onSubmit={onClose}
        onRetry={canRetry ? handleRetry : undefined}
      />
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps<
  object,
  PageProps
> = async ({ query }) => ({ props: { type: query?.errorType } })

export default ErrorPage
